import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { adminApi, formatApiErrorDetail } from "../../lib/adminApi";
import PhotoUploader from "./PhotoUploader";
import PhotoCard from "./PhotoCard";
import SortableGrid from "./SortableGrid";

export const CATEGORIES = ["Weddings", "Engagements", "Portraits", "Editorial"];

export default function AdminPortfolio({ onLogout }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["portfolio"] });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/portfolio/all");
      setPhotos(data);
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onUploaded = (photo) => {
    setPhotos((list) => [...list, photo]);
    invalidate();
  };

  const update = async (id, patch) => {
    try {
      const { data } = await adminApi.patch(`/portfolio/${id}`, patch);
      setPhotos((list) =>
        list.map((p) => {
          if (p.id === id) return data;
          if (patch.placement && p.placement === patch.placement) return { ...p, placement: null };
          return p;
        })
      );
      invalidate();
      toast.success("Photo updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await adminApi.delete(`/portfolio/${id}`);
      setPhotos((list) => list.filter((p) => p.id !== id));
      invalidate();
      toast.success("Photo removed from portfolio");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const reorder = async (ids) => {
    const byId = Object.fromEntries(photos.map((p) => [p.id, p]));
    setPhotos(ids.map((id, i) => ({ ...byId[id], order: i })));
    try {
      await adminApi.put("/portfolio/order", { ids });
      invalidate();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      load();
    }
  };

  const hero = photos.find((p) => p.placement === "hero");
  const about = photos.find((p) => p.placement === "about");
  const galleryCount = photos.filter((p) => !p.placement).length;

  return (
    <div data-testid="admin-portfolio-panel" className="grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-4">
        <PhotoUploader onUploaded={onUploaded} />
        <div className="mt-8 border border-white/10 p-5 text-xs font-light text-stone-400 space-y-2" data-testid="portfolio-summary">
          <p className="text-[9px] font-mono tracking-[0.2em] uppercase text-stone-500">Live on site</p>
          <p>Gallery frames: <span className="text-ivory">{galleryCount}</span>{galleryCount === 0 && <span className="text-stone-500"> (stock placeholders shown until you upload)</span>}</p>
          <p>Hero image: <span className="text-ivory">{hero ? hero.title : "Stock placeholder"}</span></p>
          <p>About portrait: <span className="text-ivory">{about ? about.title : "Stock placeholder"}</span></p>
        </div>
      </div>
      <div className="lg:col-span-8">
        {loading && photos.length === 0 ? (
          <p data-testid="portfolio-loading" className="text-[11px] font-mono tracking-[0.3em] uppercase text-stone-500 animate-pulse">Loading portfolio…</p>
        ) : photos.length === 0 ? (
          <p data-testid="portfolio-empty" className="font-serif text-xl italic text-stone-500">No photos yet — upload your first frame to replace the stock gallery.</p>
        ) : (
          <>
            <p className="mb-4 text-[9px] font-mono tracking-[0.2em] uppercase text-stone-500">Drag the grip on any card to reorder — the live gallery follows</p>
            <SortableGrid
              items={photos}
              onReorder={reorder}
              className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4"
              testId="portfolio-grid"
              renderItem={(p) => <PhotoCard photo={p} onUpdate={update} onDelete={remove} />}
            />
          </>
        )}
      </div>
    </div>
  );
}
